/**
 * AIZEn first-touch attribution plugin (client-only).
 *
 * On the very first page view this visitor has ever made on this site, we
 * capture the inbound UTM parameters, document.referrer, and landing path and
 * persist them into a first-party cookie for the lifetime of the visitor (1 yr).
 *
 * A separate anonymous visitor ID (`_aiz_vid`) and session ID (`_aiz_sess`)
 * are also generated so downstream systems can join first-touch data to ad
 * click records even if URL params get stripped.
 *
 * WHY CLIENT-ONLY:
 *   - Both TS and STT run SSR on Cloudflare Pages / Workers. Running
 *     attribution logic server-side risks being executed in the context of a
 *     cached request, poisoning the HTML cache or returning the wrong visitor
 *     state for subsequent visitors. The `.client.ts` filename suffix makes
 *     Nuxt 3 bundle and execute this plugin only in the browser.
 *
 * COOKIES WRITTEN:
 *   _aiz_vid   — anonymous visitor UUID (1 year, SameSite=Lax)
 *   _aiz_sess  — ephemeral session UUID (session cookie, no Max-Age)
 *   _aiz_ft    — JSON first-touch payload (1 year, only written once per visitor)
 */

const COOKIE_VID  = '_aiz_vid'
const COOKIE_SESS = '_aiz_sess'
const COOKIE_FT   = '_aiz_ft'

const YEAR_SECONDS = 60 * 60 * 24 * 365

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
  )
  return match ? decodeURIComponent(match[1]) : null
}

function writeCookie(name: string, value: string, maxAge?: number): void {
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`
  if (maxAge !== undefined) cookie += `; Max-Age=${maxAge}`
  document.cookie = cookie
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

export default defineNuxtPlugin(() => {
  // Ensure or create persistent visitor ID
  let visitorId = readCookie(COOKIE_VID)
  if (!visitorId) {
    visitorId = uuid()
    writeCookie(COOKIE_VID, visitorId, YEAR_SECONDS)
  }

  // Ensure or create session ID (no Max-Age = session cookie)
  let sessionId = readCookie(COOKIE_SESS)
  if (!sessionId) {
    sessionId = uuid()
    writeCookie(COOKIE_SESS, sessionId)
  }

  // First-touch payload — only written once per visitor; never overwritten
  if (!readCookie(COOKIE_FT)) {
    const params = new URLSearchParams(window.location.search)
    const ft = {
      utmSource:   params.get('utm_source')   ?? '',
      utmMedium:   params.get('utm_medium')   ?? '',
      utmCampaign: params.get('utm_campaign') ?? '',
      utmContent:  params.get('utm_content')  ?? '',
      utmTerm:     params.get('utm_term')      ?? '',
      ref:         document.referrer           ?? '',
      lp:          window.location.pathname    ?? '/',
      ts:          new Date().toISOString(),
    }
    writeCookie(COOKIE_FT, JSON.stringify(ft), YEAR_SECONDS)
  }
})
